'use client';
import { useEffect, useState, useCallback, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';

export interface Notification {
  id: string;
  userId: string;
  eventType: string;
  title: string;
  body: string;
  severity: 'info' | 'warning' | 'error' | 'success';
  isRead: boolean;
  readAt: string | null;
  link: string | null;
  createdAt: string;
}

interface UseNotificationsReturn {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  markAsRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  clearAll: () => Promise<void>;
}

function mapRow(row: any): Notification {
  return {
    id: row.id,
    userId: row.user_id,
    eventType: row.event_type,
    title: row.title,
    body: row.body,
    severity: row.severity,
    isRead: row.is_read,
    readAt: row.read_at,
    link: row.link,
    createdAt: row.created_at,
  };
}

/** Convert notification_frequency preference to a polling interval in ms.
 *  'realtime' uses Supabase realtime channel (no polling).
 *  Others use setInterval polling at the specified cadence.
 */
function frequencyToInterval(freq: string): number | null {
  switch (freq) {
    case 'hourly': return 60 * 60 * 1000;
    case 'daily': return 24 * 60 * 60 * 1000;
    case 'weekly': return 7 * 24 * 60 * 60 * 1000;
    default: return null; // realtime — use subscription
  }
}

export function useNotifications(
  notificationFrequency: 'realtime' | 'hourly' | 'daily' | 'weekly' = 'realtime'
): UseNotificationsReturn {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const channelRef = useRef<ReturnType<ReturnType<typeof createClient>['channel']> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Generate a stable unique channel name once per component instance.
  // Using useRef ensures the same name is used across StrictMode double-invocations
  // so the cleanup of the first run removes the channel before the second run creates it.
  const channelNameRef = useRef<string>(`notifications-realtime-${Math.random().toString(36).slice(2)}`);

  // Keep a stable ref to fetchNotifications so the realtime effect doesn't
  // re-run (and re-subscribe) every time the callback identity changes.
  const fetchNotificationsRef = useRef<() => Promise<void>>(async () => {});

  const fetchNotifications = useCallback(async () => {
    const supabase = createClient();
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      setNotifications([]);
      setLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(20);

    if (!error && data) {
      setNotifications(data.map(mapRow));
    }
    setLoading(false);
  }, []);

  // Keep the ref in sync with the latest callback without triggering the effect.
  useEffect(() => {
    fetchNotificationsRef.current = fetchNotifications;
  }, [fetchNotifications]);

  // Initial fetch
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Subscription / polling effect — depends only on notificationFrequency
  // so it never re-runs due to fetchNotifications identity changes.
  useEffect(() => {
    const interval = frequencyToInterval(notificationFrequency);

    if (interval === null) {
      // Realtime mode — subscribe once per mount.
      // Remove any existing channel first to handle StrictMode double-invocation
      // where cleanup of the first run may not have fully completed.
      const supabase = createClient();

      // Remove stale channel if it exists before creating a new one
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }

      const channel = supabase
        .channel(channelNameRef.current)
        .on(
          'postgres_changes' as any,
          { event: '*', schema: 'public', table: 'notifications' },
          () => { fetchNotificationsRef.current(); }
        )
        .subscribe();
      channelRef.current = channel;

      return () => {
        supabase.removeChannel(channel);
        channelRef.current = null;
        // Rotate the channel name so the next mount gets a fresh channel
        channelNameRef.current = `notifications-realtime-${Math.random().toString(36).slice(2)}`;
      };
    } else {
      // Digest mode — poll at the specified interval
      intervalRef.current = setInterval(
        () => { fetchNotificationsRef.current(); },
        interval
      );
      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationFrequency]);

  const markAsRead = useCallback(async (id: string) => {
    const supabase = createClient();
    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true, read_at: new Date().toISOString() })
      .eq('id', id);

    if (!error) {
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === id ? { ...n, isRead: true, readAt: new Date().toISOString() } : n
        )
      );
    }
  }, []);

  const markAllRead = useCallback(async () => {
    const supabase = createClient();
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true, read_at: new Date().toISOString() })
      .eq('user_id', user.id)
      .eq('is_read', false);

    if (!error) {
      setNotifications((prev) =>
        prev.map((n) =>
          !n.isRead ? { ...n, isRead: true, readAt: new Date().toISOString() } : n
        )
      );
    }
  }, []);

  const clearAll = useCallback(async () => {
    const supabase = createClient();
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('user_id', user.id);

    if (!error) {
      setNotifications([]);
    }
  }, []);

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  return { notifications, unreadCount, loading, markAsRead, markAllRead, clearAll };
}
